import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, SkipForward, Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * MINDFUL CONNECTION (Conexión Cuerpo / Descanso Consciente)
 *
 * Capa ADITIVA entre ejercicios. NO sustituye ningún ejercicio.
 * NO modifica la librería actual.
 *
 * "No es otro ejercicio. Es una ayuda para moverte mejor mientras descansas."
 *
 * Props:
 *   item: { id, name, instructions, duration_seconds }
 *   onSkip(): callback al saltar
 *   onComplete(): callback al completar el tiempo o pulsar "Continuar"
 */
const MindfulConnection = ({ item, onSkip, onComplete }) => {
  const duration = item?.duration_seconds || 25;
  const [timeLeft, setTimeLeft] = useState(duration);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          // Auto-completar cuando termina el tiempo
          setTimeout(() => onComplete?.(), 400);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const progressPercent = ((duration - timeLeft) / duration) * 100;
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-gradient-to-br from-emerald-950 via-teal-950 to-zinc-950 flex flex-col items-center justify-between p-6 animate-fade-in"
      data-testid="mindful-connection-overlay"
    >
      {/* Top: título */}
      <div className="w-full max-w-md text-center pt-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full mb-4">
          <Sparkles className="h-4 w-4 text-emerald-300" />
          <span className="text-sm font-medium text-emerald-200 tracking-wide uppercase">
            Conexión Cuerpo
          </span>
        </div>
        <p className="text-xs text-emerald-200/70 italic max-w-sm mx-auto">
          No es otro ejercicio. Es una ayuda para moverte mejor mientras descansas.
        </p>
      </div>

      {/* Centro: timer + nombre + texto */}
      <div className="flex flex-col items-center justify-center flex-1 w-full max-w-md">
        {/* Anillo de progreso */}
        <div className="relative w-48 h-48 mb-8">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
            <circle
              cx="100"
              cy="100"
              r={radius}
              stroke="rgba(16, 185, 129, 0.15)"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="100"
              cy="100"
              r={radius}
              stroke="rgb(16, 185, 129)"
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-6xl font-bold text-white tabular-nums" data-testid="mindful-timer">
              {timeLeft}
            </span>
          </div>
        </div>

        {/* Nombre del ejercicio */}
        <h2 className="text-2xl font-bold text-white text-center mb-3 px-4" data-testid="mindful-exercise-name">
          {item.name}
        </h2>

        {/* Instrucciones */}
        <p className="text-base text-emerald-100/90 text-center leading-relaxed px-6 max-w-sm">
          {item.instructions}
        </p>
      </div>

      {/* Bottom: botones */}
      <div className="w-full max-w-md grid grid-cols-2 gap-3 pb-6">
        <Button
          variant="outline"
          onClick={onSkip}
          className="h-14 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          data-testid="mindful-skip-btn"
        >
          <SkipForward className="h-5 w-5 mr-2" />
          Saltar
        </Button>
        <Button
          onClick={onComplete}
          className="h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          data-testid="mindful-continue-btn"
        >
          <Check className="h-5 w-5 mr-2" />
          Continuar
        </Button>
      </div>
    </div>
  );
};

export default MindfulConnection;
